import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const QRCodeHistoryPanel = ({ qrCodes }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredQRCodes = qrCodes?.filter(qr => {
    const matchesSearch = qr?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          qr?.user_profiles?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          qr?.barcode_data?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    if (filterStatus === 'active') {
      return matchesSearch && qr?.is_active && (!qr?.expires_at || new Date(qr?.expires_at) > new Date());
    } else if (filterStatus === 'expired') {
      return matchesSearch && qr?.expires_at && new Date(qr?.expires_at) <= new Date();
    } else if (filterStatus === 'inactive') {
      return matchesSearch && !qr?.is_active;
    }
    return matchesSearch;
  });

  const totalScans = qrCodes?.reduce((sum, qr) => sum + (qr?.scan_count || 0), 0);
  const activeCount = qrCodes?.filter(qr => qr?.is_active && (!qr?.expires_at || new Date(qr?.expires_at) > new Date()))?.length;
  const expiredCount = qrCodes?.filter(qr => qr?.expires_at && new Date(qr?.expires_at) <= new Date())?.length;

  return (
    <div className="space-y-6">
      {/* Analytics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total QR Codes</span>
            <Icon name="QrCode" size={20} className="text-primary" />
          </div>
          <p className="text-3xl font-bold text-foreground">{qrCodes?.length || 0}</p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total Scans</span>
            <Icon name="Scan" size={20} className="text-success" />
          </div>
          <p className="text-3xl font-bold text-foreground">{totalScans}</p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Avg Scans per QR</span>
            <Icon name="TrendingUp" size={20} className="text-info" />
          </div>
          <p className="text-3xl font-bold text-foreground">
            {qrCodes?.length > 0 ? (totalScans / qrCodes?.length)?.toFixed(1) : 0}
          </p>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-card rounded-lg p-6 border border-border">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by member name, email, or QR code ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              icon="Search"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All ({qrCodes?.length})
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'active' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Active ({activeCount})
            </button>
            <button
              onClick={() => setFilterStatus('expired')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'expired' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Expired ({expiredCount})
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'inactive' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Inactive
            </button>
          </div>
        </div>
      </div>
      {/* History Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  QR Code ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Generated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Scans
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Scan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredQRCodes?.length > 0 ? (
                filteredQRCodes?.map((qr) => {
                  const isExpired = qr?.expires_at && new Date(qr?.expires_at) <= new Date();
                  const isActive = qr?.is_active && !isExpired;

                  return (
                    <tr key={qr?.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="font-medium">{qr?.user_profiles?.full_name}</p>
                          <p className="text-sm text-muted-foreground">{qr?.user_profiles?.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-mono text-xs break-all max-w-xs">{qr?.barcode_data}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm">{new Date(qr?.generated_at || qr?.created_at)?.toLocaleDateString()}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {qr?.expires_at ? (
                          <p className="text-sm">{new Date(qr?.expires_at)?.toLocaleDateString()}</p>
                        ) : (
                          <span className="text-sm text-muted-foreground">Never</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium">{qr?.scan_count || 0}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {qr?.last_scanned_at ? (
                          <p className="text-sm">{new Date(qr?.last_scanned_at)?.toLocaleDateString()}</p>
                        ) : (
                          <span className="text-sm text-muted-foreground">Never</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          isActive
                            ? 'bg-success/10 text-success'
                            : isExpired
                            ? 'bg-warning/10 text-warning' :'bg-destructive/10 text-destructive'
                        }`}>
                          <Icon name={isActive ? 'CheckCircle' : isExpired ? 'Clock' : 'XCircle'} size={12} />
                          {isActive ? 'Active' : isExpired ? 'Expired' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Icon name="Search" size={48} className="mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground">No QR codes found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QRCodeHistoryPanel;